import * as React from 'react';
import {Ref} from "react";
import {connect} from "react-redux";
import SplitterLayout from 'react-splitter-layout';
import {AttributeTypeEnum} from "./enums/AttributeTypeEnum";
import Utils from "../../Utils";
import getAttributesEditor from "./common/AgInstanceAttributes";
import history from '../../config/history';
import * as toastr from "toastr";
import {Tab, TabList, TabPanel, Tabs} from 'react-tabs';
import {
    getBoTypeById,
    bulkUpdateInstances,
    getAllBusinessObjects,
    addAttributeValuesToEmailAttribute
} from "../../actions/boCoreActions";
import {AgGridReact} from "ag-grid-react";
import Axios from "../../config/Axios";
import urlencode from "urlencode";
import AgSelectAllHeader from "./common/AgSelectAllHeader";
import LookupInstanceModal from "./modals/LookupInstanceModal";
import LoadingModal from "./modals/LoadinModal";
import _ from "lodash";
import ReactJson from "react-json-view";
import ReactDOM from "react-dom";

const DEFAULT_NOT_PRESENT_BO_TYPE_NAME = "...";

interface IAgGrid {
    columnDefs: any;
    rowData: any;
    gridApi: any;
    columnApi: any;
}

interface State {
    businessObjectClass: any;
    businessObjects: any[],
    dataView: any;
    attributesGrid: IAgGrid;
    instancesGrid: IAgGrid;
    attributesLoaded: boolean;
    instancesLoaded: boolean;
}

interface Props {
    locale: {value: string, label: string}
}

class BulkUpdateInstances extends React.Component<Props, State> {
    private readonly tabsRef:Ref<any> = React.createRef();
    private readonly lookupInstanceModalRef:Ref<any> = React.createRef();

    constructor(props: Props) {
        super(props);
        this.state = {
            businessObjectClass: null,
            businessObjects: null,
            dataView: null,
            attributesGrid: {
                columnDefs: [],
                rowData: [],
                gridApi: null,
                columnApi: null
            },
            instancesGrid: {
                columnDefs: [],
                rowData: [],
                gridApi: null,
                columnApi: null
            },
            attributesLoaded: false,
            instancesLoaded: false
        };
    }

    componentDidMount(): void {
        if (!history.location.state) {
            this.onBackToList(false);
            return;
        }

        // @ts-ignore
        const boClass = history.location.state.businessObject;
        getBoTypeById(boClass.id).then(response => {
            this.setState({
                businessObjectClass: {
                    ...response.data,
                    uniqueName: boClass.uniqueName
                }
            }, () => this.buildAttributeGrid())
        }).catch((error) => {
            this.onBackToList(false);
            const {response} = error;
            toastr.error(response.data.message);
        });

        // @ts-ignore
        const instanceIds = history.location.state.instanceIds, dataView = history.location.state.dataView;
        Axios.instance.post(`boi/data/${boClass.id}?dataFormat=MAP&dataView=${urlencode(dataView._viewName)}`, {
            filter: JSON.stringify({
                id: instanceIds.map((id) => `${id}=`).join('|')
            })
        }).then(response => {
            this.setState({
                dataView: dataView,
                instancesLoaded: true
            }, () => this.buildInstancesGrid(response.data))
        }).catch(() => {
            this.onBackToList(false);
            toastr.error("Failed to load selected instances for bulk update operation.");
        });

        document.addEventListener('keydown', this.keydownHandler);
    }

    keydownHandler = (e: any) => {
        if(e.ctrlKey && e.altKey && e.keyCode === 8) {
            Utils.$('#back-to-list').click();
        }
    };

    parseAttributeDefaultValue = (attr: any) => {
        switch (attr.atrbType) {
            case AttributeTypeEnum.VALUE_LIST: {
                const attrValId = Number(attr.defaultValue);
                const optionalAttribute = attr.attributeValues.find(attrVal => attrVal.id === attrValId);
                const defaultValue = optionalAttribute ? optionalAttribute.name : "n/a";
                return [ defaultValue ];
            }
            case AttributeTypeEnum.EMAIL: {
                return Utils.parseEmailAttributeDefaultValue(attr.defaultValue, attr.attributeValues)
            }
            default:
                return attr.defaultValue;
        }
    };

    buildAttributeGrid = () => {
        let lookupAttributePresent = false;
        let rowData = this.state.businessObjectClass.attributes
            // .filter(attr => attr.name !== "Name" || (attr.name === "Name" && !this.state.businessObjectClass.uniqueName))
            .filter(attr => attr.atrbType !== AttributeTypeEnum.DATE)
            .map(attr => {
                const parsedAttrValue = this.parseAttributeDefaultValue(attr);
                const column = {
                    id: attr.id,
                    name: attr.name,
                    type: attr.atrbType,
                    attrKey: attr.attrKey,
                    value: parsedAttrValue,
                    defaultValue: attr.defaultValue,
                    savedValue: parsedAttrValue,
                    attributeValues: attr.attributeValues,
                    lookupAttributeId: attr.lookupAttributeId
                };
                if (column.type === AttributeTypeEnum.VALUE_LIST || column.type === AttributeTypeEnum.EMAIL) {
                    column['cellEditorParams'] = {
                        isPopup: true
                    };
                }
                if (column.type === AttributeTypeEnum.LOOKUP && !lookupAttributePresent) {
                    lookupAttributePresent = true;
                    this.loadBusinessObjects();
                }
                return column;
        });

        rowData = _.sortBy(rowData, function(item) {
            return item.name.toLowerCase() === 'name' ? 0 : 1;
        });

        const columnDefs = [
            {
                field: 'name',
                headerName: "Attribute Name",
                tooltipField: 'name',
                headerTooltip: "Attribute Name",
                width: 50,
                suppressNavigable: true
            },
            {
                field: 'type',
                headerName: "Type",
                tooltipField: 'type',
                headerTooltip: "Attribute Type",
                width: 50,
                suppressNavigable: true
            },
            {
                editable: true,
                field: 'value',
                colKey: 'value',
                headerName: "Value",
                tooltipField: 'value',
                headerTooltip: "Attribute Value",
                width: 50,
                cellEditorSelector: params => {
                    if (params.data.type === AttributeTypeEnum.BOOLEAN) {
                        const values = ['true', 'false'];
                        if (!params.data.required) {
                            values.unshift('n/a');
                        }
                        return {
                            component: 'agRichSelect',
                            params: { values }
                        };
                    }
                    return { component: 'agInstanceAttributes' };
                },
                cellRenderer: this.attrCellRenderer
            },
            {
                headerName: "",
                colId: "actions",
                cellRenderer: (params) => {
                    const eButton = document.createElement('button');
                    eButton.style.cssText = 'background: #1C1F20 ; color: #F5F5F5A3; border: 1px solid #5b5f61; height: 100%; display: flex; align-items: center;';
                    eButton.innerHTML = `<span>${params.data.setDefault ? 'Reset' : 'Set to Default'}</span>`;
                    eButton.addEventListener('click', (event) => {
                        this.onClickSetToDefault(params)
                    });
                    if (params.data.attrKey === 'name' && this.state.businessObjectClass.uniqueName) {
                        eButton.disabled = true;
                    }
                    return eButton;
                },
                editable: false,
                filter: false,
                width: 100,
                suppressSizeToFit: true
            }
        ];

        this.setState({
            attributesGrid: {
                ...this.state.attributesGrid,
                columnDefs,
                rowData
            }
        })
    };

    attrCellRenderer = (params) => {
        if (!params.node.group) {
            if (params.data.type === AttributeTypeEnum.INT || params.data.type === AttributeTypeEnum.DECIMAL) {
                return `<span>${Utils.numberFormatByLocale(params.value, this.props.locale.value)}</span>`;
            }
            if (params.data.type === AttributeTypeEnum.LOOKUP) {
                return `<span>${params.data.value.value || JSON.parse(params.data.value).value}</span>`;
            }
            if (params.data.type === AttributeTypeEnum.EMAIL) {
                const emails = Utils.parseEmailAttributeValue(params.value);
                if (emails.length > 0) {
                    const presentEmails = new Set(params.data.attributeValues.map(attrVal => attrVal.name));
                    const newlyAddedEmails = emails.filter(email => !presentEmails.has(email));
                    if (newlyAddedEmails.length > 0) {
                        const attributeValuesDto = newlyAddedEmails.map(email => {
                            return {
                                name: email,
                                description: "n/a"
                            }
                        });
                        addAttributeValuesToEmailAttribute(params.data.id, attributeValuesDto);
                    }
                }
                return `<span>${emails}</span>`;
            }
            if (params.data.type === AttributeTypeEnum.TEXT || params.data.type === AttributeTypeEnum.JSON) {
                if (params.data.attrKey === 'name' && this.state.businessObjectClass.uniqueName) {
                    return `<span>${params.value}</span>`
                } else {
                    const eDiv = document.createElement('div');
                    eDiv.innerHTML = `<span style="max-width: 70%; word-wrap: break-word; white-space: nowrap; text-overflow: ellipsis; overflow: hidden">${params.value}</span>
                            <span><button id="previewTxtBtn" style="float: right; background-color: transparent; border: transparent;">
                                <i class="fas fa-search fa-lg"></i></button></span>`;
                    const eButton = eDiv.querySelectorAll('#previewTxtBtn')[0];
                    eDiv.style.cssText = 'display: flex; white-space: initial; justify-content: space-between';
                    eButton.addEventListener('click', (event) => {
                        if (params.data.type === AttributeTypeEnum.TEXT) {
                            this.showTextArea(event,params, false);
                        } else {
                            this.showJSONComponent(event,params, false);
                        }
                    });
                    return eDiv;
                }
            }
            return `<span>${params.value}</span>`
        }
    }

    showJSONComponent(event, params, openedWithKeyCombo) {
        const openedJSONComponent = document.getElementById("json-component");
        const openedTextArea = document.getElementById("text-area");
        const saveAndCloseBtn = document.getElementById("save-and-close-btn");
        const previewHr = document.getElementById("preview-header");

        if (openedTextArea) {
            openedTextArea.remove();
        }
        if (openedJSONComponent) {
            openedJSONComponent.remove();
        }

        if (saveAndCloseBtn) {
            saveAndCloseBtn.remove();
        }

        if (previewHr) {
            previewHr.remove();
        }

        let paramsValue;
        if (!openedWithKeyCombo) {
            if (event.stopImmediatePropagation) {
                event.stopImmediatePropagation();
            }

            try {
                paramsValue = JSON.parse(params.value);
            } catch (e) {
                toastr.warning('The provided JSON is not valid')
            }
        } else {
            try {
                paramsValue = JSON.parse(event.value);
            } catch (e) {
                toastr.warning('The provided JSON is not valid')
            }
        }

        const jsonComponent = document.createElement('div');
        const saveAndClose = document.createElement('button');
        const hr = document.createElement('header');

        hr.style.position = "absolute";
        hr.style.top = "0";
        hr.style.zIndex = "1500";
        hr.style.width = "100%";
        hr.style.backgroundColor = "#2B2B2B";
        hr.style.border = "1px solid gray";
        hr.style.height = "45px";
        hr.id = "preview-header";

        saveAndClose.classList.add('btn');
        saveAndClose.classList.add('new-design');
        saveAndClose.style.position = "absolute";
        saveAndClose.style.top = "5px";
        saveAndClose.style.right = "5px";
        saveAndClose.style.zIndex = "2000";
        saveAndClose.textContent = 'Save and Close';
        saveAndClose.id = 'save-and-close-btn';

        jsonComponent.style.position = "absolute";
        jsonComponent.style.top = "0";
        jsonComponent.style.width = "100%";
        jsonComponent.style.height = "100%";
        jsonComponent.style.paddingTop = "55px";
        jsonComponent.style.zIndex = "100";
        jsonComponent.style.background = '#2B2B2B';
        jsonComponent.style.fontSize = '14px';
        jsonComponent.id = 'json-component';
        jsonComponent.tabIndex = 3;

        let newValue = paramsValue;

        const editJson = (e) => {
            newValue = e.updated_src;
        }

        const addPropToJson = (e) => {
            newValue = e.updated_src;
        }

        const onDelete = (e) => {
            newValue = e.updated_src;
        }
        let json = null;

        if (params.readonly) {
            json =  <ReactJson style={{background: '#2D3436'}}
                               enableClipboard={false}
                               theme={'tomorrow'}
                               displayDataTypes={false}
                               src={paramsValue} />

        } else {
            json =  <ReactJson style={{background: '#2D3436'}}
                               enableClipboard={false}
                               onEdit={editJson}
                               theme={'tomorrow'}
                               onAdd={addPropToJson}
                               onDelete={onDelete}
                               displayDataTypes={false}
                               src={paramsValue} />
        }

        const paneLayouts = document.getElementById("right-container");
        paneLayouts.appendChild(jsonComponent);
        paneLayouts.appendChild(jsonComponent);
        paneLayouts.appendChild(saveAndClose);
        paneLayouts.appendChild(hr);
        ReactDOM.render( json, jsonComponent );
        jsonComponent.focus();

        jsonComponent.addEventListener("keydown",  (zEvent) => {
            setTimeout(() => {
                if (zEvent.ctrlKey && zEvent.code === 'Enter') {
                    if (params.readonly) {
                        jsonComponent.remove();
                        saveAndClose.remove();
                        hr.remove();
                    } else {
                        if (!openedWithKeyCombo) {
                            params.setValue(JSON.stringify(newValue));
                        } else {
                            event.node.setDataValue(event.column.colId, JSON.stringify(newValue))
                        }
                        jsonComponent.remove();
                        saveAndClose.remove();
                        hr.remove();

                        let currentCell = this.state.attributesGrid.gridApi.getFocusedCell();
                        let finalRowIndex = this.state.attributesGrid.gridApi.paginationGetRowCount() - 1;

                        // If we are editing the last row in the grid, don't move to next line
                        if (currentCell.rowIndex === finalRowIndex) {
                            this.state.attributesGrid.gridApi.setFocusedCell(currentCell.rowIndex, 'value', null);
                            return;
                        }

                        this.state.attributesGrid.gridApi.clearFocusedCell();
                        this.state.attributesGrid.gridApi.startEditingCell({
                            rowIndex: currentCell.rowIndex + 1,
                            colKey: currentCell.column.colId
                        })
                    }
                }
            }, 50)
        });

        saveAndClose.onclick = () => {
            setTimeout(() => {
                if (params.readonly) {
                    jsonComponent.remove();
                    saveAndClose.remove();
                    hr.remove();
                } else {
                    if (!openedWithKeyCombo) {
                        params.setValue(JSON.stringify(newValue));
                    } else {
                        event.node.setDataValue(event.column.colId, JSON.stringify(newValue))
                    }
                    jsonComponent.remove();
                    saveAndClose.remove();
                    hr.remove();

                    let currentCell = this.state.attributesGrid.gridApi.getFocusedCell();
                    let finalRowIndex = this.state.attributesGrid.gridApi.paginationGetRowCount() - 1;

                    // If we are editing the last row in the grid, don't move to next line
                    if (currentCell.rowIndex === finalRowIndex) {
                        this.state.attributesGrid.gridApi.setFocusedCell(currentCell.rowIndex, 'value', null);
                        return;
                    }

                    this.state.attributesGrid.gridApi.clearFocusedCell();
                    this.state.attributesGrid.gridApi.startEditingCell({
                        rowIndex: currentCell.rowIndex + 1,
                        colKey: currentCell.column.colId
                    })
                }

            }, 50)
        }
    }

    showTextArea = (event, params, openedWithKeyCombo) => {
        const openedTextArea = document.getElementById("text-area");
        const saveAndCloseBtn = document.getElementById("save-and-close-btn");
        const openedJSONComponent = document.getElementById("json-component");
        const previewHr = document.getElementById("preview-header");

        if (openedTextArea) {
            openedTextArea.remove();
        }
        if (openedJSONComponent) {
            openedJSONComponent.remove();
        }

        if (saveAndCloseBtn) {
            saveAndCloseBtn.remove();
        }

        if (previewHr) {
            previewHr.remove();
        }

        let paramsValue;
        if (!openedWithKeyCombo) {
            if (event.stopImmediatePropagation) {
                event.stopImmediatePropagation();
            }
            paramsValue = params.value;
        } else {
            paramsValue = event.value;
        }

        const textArea = document.createElement('textarea');
        const saveAndClose = document.createElement('button');
        const hr = document.createElement('header');

        hr.style.position = "absolute";
        hr.style.top = "0";
        hr.style.zIndex = "1500";
        hr.style.width = "100%";
        hr.style.backgroundColor = "#2D3436";
        hr.style.border = "1px solid gray";
        hr.style.height = "45px";
        hr.id = "preview-header";

        saveAndClose.classList.add('btn');
        saveAndClose.classList.add('new-design');
        saveAndClose.style.position = "absolute";
        saveAndClose.style.top = "5px";
        saveAndClose.style.right = "5px";
        saveAndClose.style.zIndex = "2000";
        saveAndClose.textContent = 'Save and Close';
        saveAndClose.id = 'save-and-close-btn';

        textArea.classList.add('text-area-for-edit')
        textArea.style.top = "0";
        textArea.style.paddingTop = "47px";
        textArea.style.position = "absolute";
        textArea.style.top = "0";
        textArea.style.width = "100%";
        textArea.style.height = "100%";
        textArea.style.zIndex = "100";
        textArea.style.fontFamily = 'monospace';
        textArea.style.fontSize = '14px';
        textArea.value = paramsValue;
        textArea.id = 'text-area';
        textArea.style.resize = 'none'
        if (params.readonly) {
            textArea.readOnly = true;
        }
        const paneLayouts = document.getElementById("right-container");
        paneLayouts.appendChild(textArea);
        paneLayouts.appendChild(textArea);
        paneLayouts.appendChild(saveAndClose);
        paneLayouts.appendChild(hr);

        textArea.focus();
        textArea.addEventListener("keydown",  (zEvent) => {
            if (zEvent.ctrlKey && zEvent.code === 'Enter') {
                if (params.readonly) {
                    textArea.remove();
                    saveAndClose.remove();
                    hr.remove();
                } else {
                    if (!openedWithKeyCombo) {
                        params.setValue(textArea.value);
                    } else {
                        event.node.setDataValue(event.column.colId, textArea.value)
                    }
                    textArea.remove();
                    saveAndClose.remove();
                    hr.remove();

                    let currentCell = this.state.attributesGrid.gridApi.getFocusedCell();
                    let finalRowIndex = this.state.attributesGrid.gridApi.paginationGetRowCount() - 1;

                    // If we are editing the last row in the grid, don't move to next line
                    if (currentCell.rowIndex === finalRowIndex) {
                        this.state.attributesGrid.gridApi.setFocusedCell(currentCell.rowIndex, 'value', null);
                        return;
                    }

                    this.state.attributesGrid.gridApi.clearFocusedCell();
                    this.state.attributesGrid.gridApi.startEditingCell({
                        rowIndex: currentCell.rowIndex + 1,
                        colKey: currentCell.column.colId
                    })
                }
            } else if (params.readonly && zEvent.code === 'Escape') {
                textArea.remove();
                saveAndClose.remove();
                hr.remove();
            }
        });
        textArea.onkeydown = function (event) {
            if(event.keyCode === 9){
                let v = textArea.value, s=textArea.selectionStart, e=textArea.selectionEnd;
                textArea.value = v.substring(0, s) + '\t' + v.substring(e);
                textArea.selectionStart = textArea.selectionEnd = s + 1;
                return false;
            }
        }
        saveAndClose.onclick = () => {
            if (params.readonly) {
                textArea.remove();
                saveAndClose.remove();
                hr.remove();
            } else {
                if (!openedWithKeyCombo) {
                    params.setValue(textArea.value);
                } else {
                    event.node.setDataValue(event.column.colId, textArea.value)
                }
                textArea.remove();
                saveAndClose.remove();
                hr.remove();

                let currentCell = this.state.attributesGrid.gridApi.getFocusedCell();
                let finalRowIndex = this.state.attributesGrid.gridApi.paginationGetRowCount() - 1;

                // If we are editing the last row in the grid, don't move to next line
                if (currentCell.rowIndex === finalRowIndex) {
                    this.state.attributesGrid.gridApi.setFocusedCell(currentCell.rowIndex, 'value', null);
                    return;
                }

                this.state.attributesGrid.gridApi.clearFocusedCell();
                this.state.attributesGrid.gridApi.startEditingCell({
                    rowIndex: currentCell.rowIndex + 1,
                    colKey: currentCell.column.colId
                })
            }
        }
    }

    onClickSetToDefault = (params) => {
        this.state.attributesGrid.gridApi.forEachNode((rowNode) => {
            let rowData = rowNode.data;
            if (rowData.id === params.data.id) {
                rowData['atrbType'] = rowData.type;
                rowData['value'] = this.parseAttributeDefaultValue(rowData);
                rowData['setDefault'] = rowData['setDefault'] ? false : true;
                rowNode.setData(rowData);

                this.state.attributesGrid.gridApi.refreshCells({ force: true });
                return;
            }
        });
    };

    loadBusinessObjects = () => {
        getAllBusinessObjects().then(response => {
            this.setState({
                businessObjects: response.data
            });
        });
    };

    onAttributeGridReady = (params) => {
        params.api.sizeColumnsToFit();
        this.setState({
            attributesGrid: {
                ...this.state.attributesGrid,
                gridApi: params.api,
                columnApi: params.columnApi
            },
            attributesLoaded: true
        });
    };

    buildInstancesGrid = (responseData: any) => {
        let nameColumnPresent = false;
        responseData.content = responseData.content.map(instance => {
            const nameAttrValue = instance.data.name;
            if (nameAttrValue) {
                nameColumnPresent = true;
            }
            return {
                id: instance.id,
                updated: instance.updated,
                data: {
                    name: nameAttrValue
                }
            }
        });

        const columnDefs = [
            {
                headerName: "ID",
                width: 50,
                field: "id",
                checkboxSelection: true,
                dataType: AttributeTypeEnum.INT,
                cellRenderer: function (params) {
                    return `<label style="cursor: pointer; color: #5baeff">${params.value}</label>`
                },
                headerTooltip: "ID",
                headerComponent: AgSelectAllHeader
            },
            {
                field: 'updated',
                headerName: "Updated",
                tooltipField: 'updated',
                headerTooltip: "Updated",
                width: 50,
            }
        ];
        if (nameColumnPresent) {
            columnDefs.splice(1, 0, {
                field: 'name',
                headerName: "Name",
                tooltipField: 'name',
                headerTooltip: "Name",
                width: 50
            });
        }

        this.setState({
            instancesGrid: {
                ...this.state.instancesGrid,
                columnDefs: columnDefs,
                rowData: Utils.parseResponseDataToAgGridRowData(responseData)
            }
        })
    };

    onInstancesGridReady = (params) => {
        params.api.sizeColumnsToFit();
        params.api.forEachNode(node => node.setSelected(true));
        this.setState({
            instancesGrid: {
                ...this.state.instancesGrid,
                gridApi: params.api,
                columnApi: params.columnApi
            }
        });
    };

    onFilterChanged = (params) => {
        params.api.deselectAllFiltered();
        let filteredNodes = [];
        params.api.forEachNodeAfterFilter((node) => {
            filteredNodes.push(node);
        });
    };

    onDragEnd = () => {
        this.state.attributesGrid.gridApi.sizeColumnsToFit();
        this.state.instancesGrid.gridApi.sizeColumnsToFit();
    };

    onBackToList = (preserveFilters: boolean) => {
        const suffix = preserveFilters ? `?boc=${this.state.businessObjectClass.name.toLowerCase()}&back=true` : '';
        history.push(`${process.env.ENV_PREFIX}/managedata${suffix}`);
    };

    /**
     * Compare current value and last saved value of attribute.
     *
     * @param attributeData
     * @return true if values are equal, false otherwise
     */
    isAttributeDataChanged = (attributeData: any): boolean => {
        if (attributeData.setDefault) {
            return true;
        }
        if (attributeData.type === AttributeTypeEnum.EMAIL) {
            if (attributeData.value !== attributeData.savedValue) {
                if (typeof attributeData.savedValue === 'object') {
                    return attributeData.value !== attributeData.savedValue.join(',');
                }
                if (typeof attributeData.value === 'string') {
                    const parsedValue = '['
                        + attributeData.value
                            .split(',')
                            .map((v) => `"${v}"`)
                            .join(',')
                        + ']';
                    return parsedValue !== attributeData.savedValue;
                }
                return true;
            }
            return false;
        }
        if (attributeData.type === AttributeTypeEnum.VALUE_LIST) {
            if (attributeData.value !== attributeData.savedValue) {
                if (typeof attributeData.savedValue === 'object' && typeof attributeData.value === 'object') {
                    return attributeData.savedValue.join(',') !== attributeData.value.join(',');
                }
                return true;
            }
            return false;
        }
        return attributeData.value !== attributeData.savedValue;
    };

    parseAttributeDataValue = (attributeData: any) => {
        switch (attributeData.type) {
            case AttributeTypeEnum.EMAIL: {
                if (attributeData.value === '') {
                    return [];
                }
                return attributeData.value.split(',');
            }
            default:
                return attributeData.value;
        }
    };

    onSave = () => {
        let editedAttributes = {};
        this.state.attributesGrid.gridApi.forEachNode((rowNode) => {
            if (this.isAttributeDataChanged(rowNode.data)) {
                editedAttributes[rowNode.data.attrKey] = this.parseAttributeDataValue(rowNode.data);
            }
        });
        if (Object.keys(editedAttributes).length == 0) {
            toastr.error("No attributes modified.");
            return;
        }

        const selectedInstances = this.state.instancesGrid.gridApi.getSelectedRows();
        if (selectedInstances.length === 0) {
            toastr.error("Select at least 1 instance to update.");
            return;
        }

        bulkUpdateInstances(this.state.businessObjectClass.boKey, {attributes: editedAttributes, instanceIds: selectedInstances.map(instance => instance.id)})
            .then(response => {
                this.state.attributesGrid.gridApi.forEachNode(function(rowNode) {
                    let rowData = rowNode.data;
                    let newValue = editedAttributes[rowData.attrKey]
                    if (newValue) {
                        rowData.savedValue = newValue;
                        rowData.setDefault = false;
                        rowNode.setData(rowData)
                    }
                });
                this.state.attributesGrid.gridApi.refreshCells({ force: true });
                // update name of instances if changed
                const nameAttrValue = editedAttributes["name"];
                if (nameAttrValue) {
                    this.state.instancesGrid.gridApi.forEachNode(function(rowNode) {
                        if (rowNode.isSelected()) {
                            let rowData = rowNode.data;
                            rowData.name = nameAttrValue;
                            rowNode.setData(rowData);
                        }
                    });
                }
                toastr.success(response.data.message);
            });
    };

    nextCellForEdit = () => {
        let currentCell = this.state.attributesGrid.gridApi.getFocusedCell();
        let finalRowIndex = this.state.attributesGrid.gridApi.paginationGetRowCount() - 1;
        const suppressMoveToNext = currentCell === null ? true : currentCell.rowIndex === finalRowIndex;
        // If we are editing the last row in the grid, don't move to next line
        if (suppressMoveToNext) {
            return;
        }
        this.state.attributesGrid.gridApi.ensureIndexVisible(currentCell.rowIndex + 1);
        // scrolls to the current column
        let currentCol = this.state.attributesGrid.columnApi.getAllDisplayedColumns()[2];
        this.state.attributesGrid.gridApi.ensureColumnVisible(currentCol);
        // sets focus into the grid cell
        this.state.attributesGrid.gridApi.setFocusedCell(currentCell.rowIndex + 1, currentCol);

        this.state.attributesGrid.gridApi.startEditingCell({
            rowIndex: currentCell.rowIndex + 1,
            colKey: currentCell.column.colId
        })
    };

    onSaveLookupAttribute = (data: any) => {
        const rowNode = this.state.attributesGrid.gridApi.getRowNode(data.id);
        const rowData = rowNode.data;
        rowData.value = {
            id: data.instanceId,
            value: data.name
        };
        rowNode.setData(rowData);
    };

     attributesOnCellKeyDown = (e: any) => {
        if (e.event.ctrlKey && e.event.altKey) {
            e.api.stopEditing();
        }
        if (e.data.type === AttributeTypeEnum.TEXT || e.data.type === AttributeTypeEnum.JSON) {
            if (e.event.shiftKey && e.event.code === 'Enter') {
                e.api.stopEditing();
                if (e.data.type === AttributeTypeEnum.JSON) {
                    this.showJSONComponent(e,e,true);
                } else {
                    this.showTextArea(e,e, true);
                }
            }
        }
    }

    getSelectedInstances = () => {
         if (this.state.instancesGrid.gridApi) {
             return this.state.instancesGrid.gridApi.getSelectedRows().length;
         }
         return 'No Selected Instances';
    }

    render() {
        if (!this.state.attributesLoaded && !this.state.instancesLoaded) {
            return <LoadingModal open={true}/>
        }
        const boTypeName = this.state.businessObjectClass !== null ? this.state.businessObjectClass.name : DEFAULT_NOT_PRESENT_BO_TYPE_NAME;
        return (
            <div id={"bulkInstanceEditor"} style={{width: "100%", height: "100%"}}>
                <div style={{
                    width: "100%",
                    display: "flex",
                    height: 50,
                    paddingTop: 5,
                    paddingBottom: 5,
                    paddingLeft: 5,
                    paddingRight: 5,
                    borderBottom: "2px solid black"
                }} className={'background-gradient'}>
                    <h2 className={'bulk-updating-header-label'}>Bulk Updating: {this.getSelectedInstances()} Instances</h2>
                    <div style={{
                        display: "flex",
                        height: 34,
                        float: "right",
                        marginLeft: 5,
                        marginTop: 3,
                        marginRight: 1
                    }}>
                        <div style={{display: "flex"}}>
                            <button id={'save-instance-btn'} onClick={this.onSave} style={{marginRight: 5}}
                                    className={"btn btn-primary"} title={'CTRL + ALT + S'}>
                                <i className="fas fa-save"> <strong className={'shortcut-btn-letters'}>S</strong>ave</i>
                            </button>
                            <button id={'back-to-list'} onClick={() => this.onBackToList(true)}
                                    className={"btn btn-new-design"} title={'CTRL + ALT + BACKSPACE'}>
                                <i className="fas fa-backward"> Back To List</i>
                            </button>
                        </div>
                    </div>
                </div>
                <div id="bulk-update-splitter-layout-wrapper" style={{height: "calc(100% - 58px)"}}>
                    <SplitterLayout onDragEnd={this.onDragEnd} primaryIndex={1} style={{
                        height: "calc(100% - 50px)", width: "calc(100% - 30px)"}}>
                        <div
                            className={"ag-theme-balham-dark ag-grids"}
                            style={{width: "100%", height: "100%", minWidth: 400, background: "#2D3436", paddingTop: 5, paddingBottom: 5, paddingRight:5, paddingLeft:5}}
                        >
                            <AgGridReact
                                onGridReady         = {this.onAttributeGridReady.bind(this)}
                                onFirstDataRendered = {this.onAttributeGridReady.bind(this)}
                                columnDefs          = {this.state.attributesGrid.columnDefs}
                                rowData             = {this.state.attributesGrid.rowData}
                                onFilterChanged     = {this.onFilterChanged.bind(this)}
                                defaultColDef={{
                                    filter: "agTextColumnFilter",
                                    sortable: true,
                                    resizable: true
                                }}
                                gridOptions={{
                                    floatingFilter: true,
                                    animateRows: true,
                                    debug: false,
                                    groupUseEntireRow: true,
                                    groupSelectsFiltered: true,
                                    suppressDragLeaveHidesColumns: true,
                                    groupDefaultExpanded: -1,
                                    singleClickEdit: true,
                                    getRowNodeId: function (data) {
                                        return data.id;
                                    },
                                    components: {
                                        //@ts-ignore
                                        agInstanceAttributes: getAttributesEditor()
                                    },
                                    rowClassRules: {
                                        'attributes-grid-edited-attr-row': (params) => {
                                            return this.isAttributeDataChanged(params.data);
                                        }
                                    },
                                    stopEditingWhenGridLosesFocus: true,
                                    onCellEditingStarted: (params) => {
                                        if (params.data.type === AttributeTypeEnum.LOOKUP) {
                                            params.api.stopEditing();
                                           // @ts-ignore
                                            this.lookupInstanceModalRef.current.show(params.data, this.state.businessObjects);
                                        }
                                        if (params.data.type === AttributeTypeEnum.TEXT) {
                                            if (params.data.attrKey.toLowerCase() === 'name' && this.state.businessObjectClass.uniqueName) {
                                                params.api.stopEditing();
                                            }
                                        }
                                    },
                                    onCellKeyDown: this.attributesOnCellKeyDown,
                                }}
                            />
                        </div>
                        <div id={'right-container'} className={"ag-theme-balham-dark ag-grids"} style={{width: "100%", height: "100%", minWidth: 400, paddingTop: 5}}>
                            <Tabs id={'bulk-page-tabs'} ref={this.tabsRef}>
                                <TabList>
                                    <Tab title={'CTRL + ALT + I'}>
                                        <i className="fas fa-table"> Records to be updated</i>
                                    </Tab>
                                </TabList>
                                <TabPanel forceRender={true}>
                                    <div
                                        id={"instancesGrid"}
                                        className={"ag-theme-balham-dark ag-grids"}
                                        style={{width: "100%", height: "calc(100vh - 145px)", paddingLeft: 5, paddingRight: 5}}
                                    >
                                        <AgGridReact
                                            onGridReady         = {this.onInstancesGridReady.bind(this)}
                                            onFirstDataRendered = {this.onInstancesGridReady.bind(this)}
                                            columnDefs          = {this.state.instancesGrid.columnDefs}
                                            rowData             = {this.state.instancesGrid.rowData}
                                            defaultColDef={{
                                                filter: "agTextColumnFilter",
                                                sortable: true,
                                                resizable: true
                                            }}
                                            gridOptions={{
                                                floatingFilter: true,
                                                animateRows: true,
                                                debug: false,
                                                groupUseEntireRow: true,
                                                groupSelectsFiltered: true,
                                                suppressDragLeaveHidesColumns: true,
                                                groupDefaultExpanded: -1,
                                                singleClickEdit: true,
                                                getRowNodeId: function (data) {
                                                    return data.id;
                                                },
                                                rowSelection: 'multiple'
                                            }}
                                        />
                                    </div>
                                </TabPanel>
                            </Tabs>
                        </div>
                    </SplitterLayout>
                </div>
                <LookupInstanceModal {...{save: this.onSaveLookupAttribute}} nextCellForEdit={this.nextCellForEdit} ref={this.lookupInstanceModalRef}/>
            </div>
        );
    }
}

const mapStateToProps = (state: any, ownProps: any) => {
    return {
        locale: state.auth.locale
    }
};

const mapDispatchToProps = () => {
    return {}
};

export default connect(mapStateToProps, mapDispatchToProps)(BulkUpdateInstances)



// WEBPACK FOOTER //
// ./app/components/bo-core/BulkUpdateInstances.tsx